<template>
  <div style="height: 100%">
    <div style="padding: 0 16px" v-if="!isExternalAddMode">
      <c-label :color="$theme.COLOR.UI.UI13" :weight="500" :size="16" :lineHeight="16"
        >{{ configTitle }}
      </c-label>
      <div style="height: 24px"></div>
    </div>
    <c-scroll
      style="padding: 0 16px"
      :style="{ maxHeight: !isExternalAddMode ? '656px' : '702px' }"
    >
      <div class="area">
        <ConfigTitle required class="title"
          >{{ $util.capitalize($t('label.widgetName')) }}
        </ConfigTitle>
        <lego-text-field
          ref="widgetName"
          :rules="validator.widgetName"
          :modelValue="widgetName"
          counter="100"
          showCounter
          :guide="$t('hint.common') + ' ' + $t('hint.notStartWithDot')"
          @update:modelValue="(value) => changeWidgetName(value)"
          isFreeText
        />
      </div>
      <BoxRegion class="area" />
      <div v-if="isExternalAddMode" class="area">
        <ConfigTitle required class="title">{{ $t('page.dashboard.dashboardSelect') }}</ConfigTitle>
        <lego-dropdown
          :placeholder="$t('message.select3', [$t('menu.customDashboard').toLowerCase()])"
          :items="dashboardItems"
          ref="dashboardModel"
          :rules="validator.dashboardModel"
          v-model="dashboardModel"
          :noDataText="$t('label.noDataCustomDashboard')"
          @update:modelValue="
            onConfigChange();
            changeData();
          "
        />
      </div>
      <div class="area">
        <ConfigTitle required class="title"
          >{{ $util.capitalize($t('label.product')) }}
        </ConfigTitle>
        <scpm-tree-dropdown
          v-model="resourceTypeModel"
          :items="productTypeGroups"
          @update:modelValue="
            selectResourceType();
            changeData();
          "
          :disabled="isDisabled"
          ref="resourceTypeModel"
          :rules="validator.resourceTypeModel"
        />
        <lego-dropdown
          :items="childResourceTypeItems"
          v-model="childResourceTypeModel"
          v-if="isChildResourceType && childResourceTypeItems.length > 0"
          style="margin-top: 10px"
          @update:modelValue="
            selectChildResourceType();
            changeData();
          "
          :disabled="isDisabled || !isResourceTypeAuth"
        />
      </div>
      <div class="area monitoring-target" :key="resourceTypeModel">
        <ConfigTitle required class="title title-monitoring"
          >{{ $util.capitalize($t('label.monitoring')) }} {{ $t('label.target') }}
        </ConfigTitle>
        <scpm-select-box
          ref="productModel"
          v-model="productModel"
          :rules="validator.productModel"
          underMessage
          isMontargetCustomCommon
          :items="productItems"
          :maxSelect="!isCheckObjectTypeItems ? maxSelectTarget : ''"
          :messageErrorMaxSelect="
            $t('message.selectCountErrorMessage', {
              max: maxSelectTarget.toString(),
              field: $t('searchLabel.monTargets')
            })
          "
          :dropdownErrorMessage="true"
          :searchPlaceholder="$t('searchLabel.searchLike', { VPC: $t('searchLabel.monTarget') })"
          :selectedPlaceholder="
            $t('message.select3', [$t('header.monitoringTarget').toLowerCase()])
          "
          showOnlyText
          hasSearchField
          @update:modelValue="
            selectProduct();
            changeData();
          "
          :disabled="isDisabled || !isResourceTypeAuth"
          @changeHeightDownwrap="changeHeightDownwrap"
        />
        <scpm-select-box
          v-if="showObjectTypeItems"
          ref="objectTypeModel"
          v-model="objectTypeModel"
          :items="objectTypeItems"
          :maxSelect="maxSelectTarget"
          :messageErrorMaxSelect="
            $t('message.selectCountErrorMessage', {
              max: maxSelectTarget.toString(),
              field: $t('searchLabel.monTargets')
            })
          "
          :dropdownErrorMessage="true"
          :selectedPlaceholder="
            $t('message.select3', [$t('header.monitoringTarget').toLowerCase()])
          "
          :rules="validator.objectTypeModel"
          showOnlyText
          hasSearchField
          :disabled="isDisabled || !isResourceTypeAuth"
          class="object-type-model"
          style="margin-top: 8px"
          @update:modelValue="
            selectObject('objectTypeModel');
            changeData();
          "
          @changeHeightDownwrap="changeHeightDownwrap"
        />
      </div>
      <div class="area performance">
        <ConfigTitle required>
          {{ $util.capitalize($t('label.performance')) }} {{ $t('label.item') }}
          <template
            v-slot:message
            v-if="isValidMessage.metricModel && selectedMetrics.length === 0"
          >
            <div class="message">
              {{ $t('message.select1', [$t('label.performance') + ' ' + $t('label.item')]) }}
            </div>
          </template>
        </ConfigTitle>
        <scpm-tree-checkbox-dropdown
          ref="metricModel"
          :items="metrics"
          isPerformanceItemOfCustomCommon
          :disabled="!productItems.length"
          :needResetMetrics="isResetData"
          :selectedMetrics="selectedMetrics"
          :multiSelect="false"
          disableInputMutation
          :searchPlaceholder="$t('searchLabel.searchLike', { VPC: $t('searchLabel.perfItem') })"
          :selectedPlaceholder="$t('message.select3', [$t('title.performanceItem').toLowerCase()])"
          searchKey="metricName"
          @update:modelValue="selectMetric"
          @changeHeightDownwrap="changeHeightDownwrap"
        />
      </div>

      <div class="area" v-if="!disableIndividualItem && selectedMetrics.length">
        <ConfigTitle :required="true" class="title"
          >{{ $util.capitalize($t('label.individual')) }}
          {{ $util.capitalize($t('label.item')) }}
        </ConfigTitle>
        <scpm-select-box
          ref="objectModel"
          v-model="objectModel"
          showTop
          :items="getListIndividualItem"
          :rules="validator.objectModel"
          :searchPlaceholder="$t('searchLabel.searchLike', { VPC: $t('label.individualItem') })"
          :messageErrorMaxSelect="
            $t('message.selectCountErrorMessage', {
              max: 100,
              field: $t('label.individualItem').toLowerCase()
            })
          "
          :dropdownErrorMessage="true"
          :selectedPlaceholder="$t('message.select3', [$t('label.individualItem').toLowerCase()])"
          showOnlyText
          hasSearchField
          @update:modelValue="
            selectObject('objectModel');
            changeData();
          "
        />
      </div>

      <div v-if="useTargetsMethod" class="area">
        <ConfigTitle required class="title">
          {{ $util.capitalize($t('label.statistics1')) }}
          <template
            v-slot:message
            v-if="isValidMessage.targetsMethodModel && targetsMethodModel == ''"
          >
            <div class="message">
              {{ $t('message.select1', [$t('label.statistics1') + ' ' + $t('label.item')]) }}
            </div>
          </template>
        </ConfigTitle>
        <lego-segment-wrapper
          v-model="targetsMethodModel"
          ref="targetsMethodModel"
          style="width: 100%"
          @update:modelValue="
            onConfigChange();
            changeData();
          "
        >
          <lego-segment-button value="avg" style="width: 25%">avg</lego-segment-button>
          <lego-segment-button value="min" style="width: 25%">min</lego-segment-button>
          <lego-segment-button value="max" style="width: 25%">max</lego-segment-button>
          <lego-segment-button value="raw" style="width: 25%" :disabled="targetsMethodRawDisabled"
            >raw
          </lego-segment-button>
        </lego-segment-wrapper>
      </div>
      <div v-if="useThresholdRange || useThresholdValue" class="area">
        <ConfigTitle class="title"
          >{{ $util.capitalize($t('label.option')) }} {{ $t('label.add1') }}
        </ConfigTitle>
        <lego-checkbox
          v-if="useThresholdRange"
          v-model="showThresholdRange"
          class="check_vertical"
          @update:modelValue="
            selectShowThresholdRange();
            changeData();
          "
          >{{ $t('label.riskRange') }}
        </lego-checkbox>
        <div v-if="showThresholdRange">
          <div class="threshold-range">
            <lego-text-field
              style="width: 105px; min-width: 105px; margin-left: 24px"
              ref="thresholdStart"
              :rules="validator.thresholdStart"
              v-model="thresholdStart"
              :isHideSpecialCharacter="true"
              @keyup.prevent="checkNumberInput($event, 'thresholdStart')"
              @update:modelValue="changeData()"
              counter="100"
              :guide="$t('hint.onlyNumericAndDot')"
            />
            <span style="margin: 10px">~</span>
            <lego-text-field
              style="width: 105px; min-width: 105px"
              ref="thresholdEnd"
              :rules="validator.thresholdEnd"
              v-model="thresholdEnd"
              :isHideSpecialCharacter="true"
              @keyup.prevent="checkNumberInput($event, 'thresholdEnd')"
              @update:modelValue="changeData()"
              counter="100"
              :guide="$t('hint.onlyNumericAndDot')"
            />
            <span style="margin: 10px">{{ metricModel.metricUnit }}</span>
          </div>
          <div
            class="message"
            style="text-align: left; margin-left: 23px"
            v-if="isValidMessage.useThresholdRange"
          >
            {{ isValidMessage.useThresholdRangeText }}
          </div>
        </div>
        <lego-checkbox
          v-if="useThresholdValue"
          v-model="showThresholdValue"
          class="check_vertical"
          @update:modelValue="selectShowThresholdValue()"
          >{{ $t('label.riskCriteria') }}
        </lego-checkbox>
        <div v-if="showThresholdValue">
          <div class="threshold-range">
            <lego-text-field
              style="width: 100px; min-width: 100px; margin-left: 24px"
              ref="thresholdValue"
              :rules="validator.thresholdValue"
              v-model="thresholdValue"
              :isHideSpecialCharacter="true"
              :mutateInput="this.$util.replaceToOnlyNumberDot"
              @keyup.prevent="checkNumberInput($event, 'thresholdValue')"
              @update:modelValue="changeData()"
              counter="100"
              :guide="$t('hint.onlyNumericAndDot')"
            />
            <span style="margin: 10px">{{ metricModel.metricUnit }}</span>
            <lego-radio-wrapper
              v-model="comparisonOperator"
              ref="comparisonOperator"
              :rules="validator.comparisonOperator"
              style="display: inline"
              @update:modelValue="
                onConfigChange();
                changeData();
                clickOutside();
              "
            >
              <lego-radio :value="'GE'">
                <div class="radioItemLabel">
                  {{ $util.capitalize($t('label.above')) }}
                </div>
              </lego-radio>
              <lego-radio :value="'LE'">
                <div class="radioItemLabel">
                  {{ $util.capitalize($t('label.under')) }}
                </div>
              </lego-radio>
            </lego-radio-wrapper>
          </div>
          <div
            class="message"
            style="text-align: left; margin-left: 23px"
            v-if="isValidMessage.thresholdValue"
          >
            {{ isValidMessage.thresholdValueText }}
          </div>
        </div>
      </div>
      <div style="height: 1px" v-if="isChangeBolet"></div>
    </c-scroll>
  </div>
</template>
<script>
import configMixin from './configMixin';
import _ from 'lodash';
import {
  getDashboardDatas,
  getResourceTypeDatas,
  getProductSummary,
  getObjectDatas,
  getMetricDatas
} from '../serviceApi';
import { checkObjectTypesNonSub } from '@/containers/utils/utils.js';
import { SUB_OBJECT_TYPES_WITH_K8S, PRODUCT_TYPES } from '@/containers/utils/constant';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import BoxRegion from './BoxRegion.vue';
import { useProductTypeGroups } from '@/composables/useProductTypeGroups.js';
import { GLOBAL_CDN } from '@/containers/utils/constant';

const { getProductTypeGroups } = useProductTypeGroups();

export default {
  props: ['configItem', 'chartType', 'searchDate'],
  mixins: [configMixin],
  components: {
    BoxRegion
  },
  emits: ['configModify', 'click-outside'],
  data() {
    return {
      isChangeBolet: false,
      productTypeGroups: [],
      dashboardItems: [], // 대시보드 목록 (프로젝트 권한이 있는)
      dashboardModel: '', // 선택한 대시보드
      useTargetsMethod: false, // 통계 옵션 display 여부
      widgetName: '', // 위젯명
      resourceTypeItems: [], // 상품 목록
      resourceTypeModel: '', // 선택한 상품 코드
      resourceTypeModelTmt: '',
      childResourceTypeItems: [], // 하위 상품 목록
      childResourceTypeModel: '', // 선택한 하위 상품 코드
      productItems: [], // 모니터링 대상 목록
      productModel: [], // 선택한 모니터링 대상 목록
      objectTypeItems: [], // 하위 모니타링 대상 목록 (k8s & lb & objectTypeItemsㅋ)
      objectTypeModel: [], // 선택한 하위 모니터링 대상 목록 (k8s & lb)
      objectItems: [], // 개별항목 목록
      objectModel: [], // 선택한 개별항목 목록
      metrics: [], // 성능항목 목록
      metricModel: {}, // 선택한 성능항목 Object
      selectedMetrics: [], // 선택한 성능항목 metric key
      targetsMethodModel: null, // 선택한 통계항목
      listIndividual: [],
      disableIndividualItem: false,
      showThresholdRange: false, // 위험 구간 표시 체크 여부
      useThresholdRange: false, // 워험 구간 표시 display 여부
      thresholdStart: '', // 위험구간 시작 value
      thresholdEnd: '', // 위험구간 종료 value

      showThresholdValue: false, // 위험도 표시 체크 여부
      useThresholdValue: false, // 위험도 표시 display 여부
      thresholdValue: '', // 위험도 value
      comparisonOperator: '', // 위험도 value 이상 & 이하

      isValidMessage: {
        productItems: false, // 모니터링 대상 valid message 출력 여부
        productItemsText: '', // 모니터링 대상 valid message
        metricModel: false, // 성능 항목 valid message 출력 여부
        targetsMethodModel: false, // 통계 항목 valid message 출력 여부
        useThresholdRange: false, // 위험도 입력 valid message 출력여부
        thresholdValue: false, // 위험도 입력 valid message 출력여부
        thresholdValueText: '', // 위험도 입력 valid message 출력여부
        useThresholdRangeText: '' // 위험도 입력 valid message
      },

      validator: {
        widgetName: [this.checkWidgetName],
        dashboardModel: [this.checkDashboardModel],
        resourceTypeModel: [this.checkResourceTypeModel],
        productModel: [this.checkProductModel],
        objectTypeModel: [this.checkObjectTypeModel],
        objectModel: [this.checkObjectModel],
        thresholdStart: [this.checkThresholdStart],
        thresholdEnd: [this.checkThresholdEnd],
        thresholdValue: [this.checkThresholdValue],
        comparisonOperator: [this.checkComparisonOperator]
      },
      maxSelectTarget: 10,
      tempConfigInfos: [], // 위젯 설정정보 temp
      isObjectExist: 'N', // 개별항목 존재 여부
      isResourceTypeAuth: true, // 상품유형 권한 여부
      isProductAuth: true, // 상품(모니터링 대상) 권한 여부
      isResetData: false,
      validating: false
    };
  },
  inject: [],
  created() {
    this.isObjectExist = _.get(
      this.defaultConfig,
      'chartAttr.widget.options.isObjectExist',
      this.isObjectExist
    );
  },
  watch: {
    selectedMetrics: {
      handler(v) {
        this.changeData();
        if (v.length) {
          this.isResetData = false;
          if (this.isModifyMode && !v[0].manual) {
            this.disableIndividualItem = v[0].disableIndividualItem;
          } else {
            this.disableIndividualItem = this.checkDisableIndividualItem(v[0]);
          }
        } else this.isResetData = true;
      },
      immediate: true,
      deep: true
    },
    objectModel: {
      handler(val, preVal) {
        if (!_.isEqual(val, preVal)) this.changeData();
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    // 하위 상품 존재 여부
    isChildResourceType() {
      return [
        PRODUCT_TYPES.KUBERNETES,
        PRODUCT_TYPES.LOAD_BALANCER,
        PRODUCT_TYPES.DATA_OPS,
        PRODUCT_TYPES.DATA_FLOW,
        PRODUCT_TYPES.QUICK_QUERY,
        PRODUCT_TYPES.DATA_WRANGLER
      ].includes(this.resourceTypeModel);
    },
    isDisabled() {
      return !this.isAccountAuth;
    },
    isKuber() {
      return this.resourceTypeModel.toLowerCase() === 'kubernetes';
    },
    isK8sObjectType() {
      return this.isKuber && SUB_OBJECT_TYPES_WITH_K8S.includes(this.childResourceTypeModel);
    },
    getListIndividualItem() {
      if (this.isK8sObjectType) {
        return this.listIndividual;
      }
      return this.objectItems;
    },
    // 통계 설정 Raw 선택 disabled 여부
    targetsMethodRawDisabled() {
      return (
        !(this.productModel.length === 1) ||
        (this.objectItems.length > 0 && !(this.objectModel.length === 1)) ||
        (this.isChildResourceType &&
          this.objectTypeItems.length > 0 &&
          !(this.objectTypeModel.length === 1))
      );
    },
    // 개별상품 출력 여부
    showObjectTypeItems() {
      return (
        this.isChildResourceType &&
        this.$util.isNotEmpty(this.productModel) &&
        checkObjectTypesNonSub(this.resourceTypeModel, this.childResourceTypeModel)
      );
    },
    isCheckObjectTypeItems() {
      return checkObjectTypesNonSub(this.resourceTypeModel, this.childResourceTypeModel);
    },
    checkEmptyObjectTypeItems() {
      if (!this.showObjectTypeItems) return false;
      return this.objectTypeModel.length === 0;
    }
  },
  methods: {
    async getListProductAndPerformanceItem(reset = true, isInit = false) {
      this.resetValidate();
      this.isProductAuth = true;
      this.$util.validate([this.$refs.resourceTypeModel, this.$refs.productModel]);

      if (this.isAccountAuth) {
        if (reset) this.reset(6);
        await this.selectResourceType(reset, isInit);
      } else {
        this.reset(6);
        this.onConfigChange();
      }
    },
    /** 상품유형 선택 */
    async selectResourceType(reset = true, isInit = false) {
      if (reset) {
        this.showThresholdRange = false;
        this.thresholdStart = '';
        this.thresholdEnd = '';
        this.showThresholdValue = false;
        this.thresholdValue = '';
        this.comparisonOperator = '';
        this.targetsMethodModel = 'avg';
      }
      this.resetValidate();
      this.isProductAuth = true;

      if (!isInit) {
        this.isResourceTypeAuth = await this.checkProductSubTypeAuth(this.resourceTypeModel);
      }

      this.$util.validate([this.$refs.resourceTypeModel, this.$refs.productModel]);

      if (this.isResourceTypeAuth) {
        if (reset) this.reset(5);
        // 하위 상품유형
        if (this.isChildResourceType) await this.setChildResourceTypeDatas();

        // 모니터링 대상목록
        await this.setProductsDatas();

        // 성능항목
        await this.setMetricDatas();
      } else {
        this.reset(5);
      }
      this.onConfigChange();
    },

    /** k8s, LB 하위 상품 목록 선택 */
    async selectChildResourceType() {
      this.resetValidate();
      this.reset(4);

      // 모니터링 대상목록
      await this.setProductsDatas();
      // 성능항목
      await this.setMetricDatas();

      this.onConfigChange();
    },

    /** 통계항목 reset */
    resetTargetsMethod() {
      if (
        this.useTargetsMethod &&
        this.targetsMethodModel === 'raw' &&
        !(this.objectTypeModel.length === 1)
      ) {
        this.targetsMethodModel = '';
      }
    },

    /** 모니터링 대상 선택 */
    async selectProduct() {
      this.resetValidate('productModel');
      this.isProductAuth = true;

      await this.setProductAuth();
      this.$util.validate([this.$refs.productModel]);

      this.reset(3);

      if (this.isProductAuth) {
        if (this.productModel.length > this.maxSelectTarget) {
          //this.productModel = [];
          this.isValidMessage.productItemsText = this.$t('message.selectCount', [
            this.maxSelectTarget.toString()
          ]);
          this.isValidMessage.productItems = true;
        } else {
          this.isValidMessage.productItemsText = '';
          this.isValidMessage.productItems = false;
        }

        // 통계항목 reset
        this.resetTargetsMethod();
        // 하위 모니터링 상품
        if (this.productModel.length > 0) {
          await this.setChildProductsDatas();
        }
      }

      await this.setMetricDatas();
      this.onConfigChange();
    },

    /** 모니터링 대상 하위 상품 선택 && 개벌항목 선택 */
    async selectObject(mode) {
      this.resetValidate(mode);
      this.resetTargetsMethod();

      if (this.selectedMetrics.length && !this.disableIndividualItem) {
        await this.getIndividualItemData();
      }

      if (
        (!this.disableIndividualItem && this.objectModel.length) ||
        (this.disableIndividualItem && this.showObjectTypeItems && this.objectTypeModel.length) ||
        !this.showObjectTypeItems
      ) {
        return this.onConfigChange();
      }

      if (!this.objectModel.length || (this.showObjectTypeItems && !this.objectTypeModel.length)) {
        return this.$emit('configModify', []);
      }
    },

    checkDisableIndividualItem(metricItem) {
      let visible = false;
      if (!metricItem.isObjectExist || metricItem.isObjectExist === 'N') visible = true;
      if (metricItem.isObjectExist === 'Y' && metricItem.disableObject === 'Y') visible = true;
      return visible;
    },

    async getIndividualItemData() {
      if (!this.productModel.length || (this.showObjectTypeItems && !this.objectTypeModel.length)) {
        this.listIndividual = [];
        this.objectModel = [];
      } else if (this.isChildResourceType) {
        const params = {
          productIdList: _.map(this.productModel, (m) => m.productSq),
          productObjectType: this.childResourceTypeModel,
          productSubName: this.objectTypeModel.map((e) => e.key),
          queryStartDt: this.searchDate.queryStartDt,
          queryEndDt: this.searchDate.queryEndDt
        };
        await getObjectDatas('k8s', this.resourceTypeModel, params).then((res) => {
          if (res && res.length) {
            this.listIndividual = res.map((item) => {
              return {
                ..._.omit(item, ['managerName', 'podLabel']),
                key: item.objectName,
                text: item.objectDisplayName
              };
            });
            const items = this.$util.replaceArr(res, {
              objectName: 'key',
              objectDisplayName: 'text'
            });
            this.objectModel = this.objectModel.filter((item) => {
              return items.some((other) => other.key === item.key);
            });
          }
        });
      }
    },

    /** 성능항목 선택 */
    selectMetric(item) {
      if (!item.length) return;
      if (this.$refs.metricModel) {
        this.$refs.metricModel.toggleItemOuter();
      }
      const metricData = item[0];
      this.changeData();
      this.resetValidate('metricModel');

      if (
        !this.selectedMetrics.length ||
        metricData.metricKey !== this.selectedMetrics[0].metricKey
      )
        this.reset(2);

      this.isObjectExist = metricData.isObjectExist; // 개별항목 존재 여부
      this.selectedMetrics = [];
      const newItem = { ...metricData, manual: true };
      this.selectedMetrics.push(newItem);

      this.disableIndividualItem = this.checkDisableIndividualItem(newItem);

      // support to compare data when change type custom widget
      this.setMetricDataForPerformanceItem(newItem.metricName, newItem.metricKey);

      if (this.showObjectTypeItems && !this.objectTypeModel.length) {
        return;
      }

      this.onConfigChange();

      if (this.$refs.objectModel) this.$refs.objectModel.toggleItemOuter();
      if (this.isK8sObjectType) {
        this.getIndividualItemData();
      } else {
        this.setObjectItems(metricData.metricKey);
      }
    },

    /** 위험구간 표시 체크 */
    selectShowThresholdRange() {
      this.clickOutside();
      this.thresholdStart = '';
      this.thresholdEnd = '';
      this.onConfigChange();
      this.checkUseThresholdRange();
    },
    clickOutside() {
      legoEventBus.$emit('click-outside');
    },

    /** 위험도 표시 체크 */
    selectShowThresholdValue() {
      this.isValidMessage.thresholdValue = false;
      this.clickOutside();
      this.thresholdValue = '';
      if (this.$util.isEmpty(this.comparisonOperator)) {
        this.comparisonOperator = 'GE';
      }
      this.onConfigChange();
      this.changeData();
    },

    /** 대시보드 목록 셋팅 */
    async setDashboardData() {
      await getDashboardDatas().then((res) => {
        this.dashboardItems = this.$util.convertArr(res.contents, {
          dashboardName: 'text',
          dashboardId: 'value'
        });
      });
    },

    /** 상품유형 셋팅 */
    async setResourceTypeData() {
      await getResourceTypeDatas().then(async (res) => {
        this.$util.convertArr(res, { productTypeName: 'text', productTypeCode: 'value' });
        this.resourceTypeItems = res;
        this.productTypeGroups = await getProductTypeGroups(res, 'parentProductTypeName', [
          GLOBAL_CDN
        ]);
        if (!this.resourceTypeModel) this.resourceTypeModel = this.resourceTypeItems[0].value;
      });
    },

    async checkProductSubTypeAuth(productTypeCode) {
      const authData = await this.$store.dispatch('authStore/checkProductSubTypeAuthorization', {
        productTypeCode
      });
      return authData.type === this.$const.AuthorizationResult.ALLOW;
    },

    /** 하위 상품유형 셋팅 */
    async setChildResourceTypeDatas() {
      this.childResourceTypeItems = await this.$http.product.getListProductObjectTypes(
        this.resourceTypeModel
      );
      if (!this.childResourceTypeModel)
        this.childResourceTypeModel = this.childResourceTypeItems[0].value;
    },

    /** 모니터링 대상 셋팅 */
    async setProductsDatas() {
      await getProductSummary(this.resourceTypeModel).then((data) => {
        data.map((item, index) => {
          item['idx'] = index;
          return item;
        });
        this.productItems = this.$util.replaceArr(data, {
          productName: 'text',
          resourceId: 'key',
          productSq: 'productSq',
          idx: 'idx'
        });

        // 기존 선택한 모니터링 대상이 삭제될 경우 방어 코드
        if (this.productModel.length > 0) {
          this.productModel = this.productModel.filter((item) => {
            return this.productItems.some((other) => other.key === item.key);
          });
        }
      });
    },

    /** 모니터링 대상(상품) 권한 체크 */
    async setProductAuth() {
      if (this.productModel.length > 0) {
        const productResourceIds = this.productModel.map((v) => v.key);
        const listDataCheckAuth = this.productModel.map((v) => {
          return {
            productResourceId: v.key,
            productName: v.text
          };
        });
        const param = {
          productTypeCode: this.resourceTypeModel,
          actionName: this.$const.AuthorizationAction.MONITOR_VIEW,
          productResourceIds,
          listDataCheckAuth
        };

        const res = await this.$store.dispatch('authStore/checkProductResource', param);
        if (res.type === this.$const.AuthorizationResult.ALLOW) {
          return (this.isProductAuth = true);
        }

        //Function for check resource ids error, if it exits, it doesn't need to check others target
        if (!res.resourceIds || !res.resourceIds.length) return;

        this.productModel = this.productModel.filter((item) => !res.resourceIds.includes(item.key));
        this.isProductAuth = false;

        await this.setProductAuth();
      }
    },

    /** 하위 상품(모니터링 대상) 셋팅 */
    async setChildProductsDatas() {
      if (
        (this.resourceTypeModel === PRODUCT_TYPES.KUBERNETES &&
          this.childResourceTypeModel !== 'cluster') ||
        ([PRODUCT_TYPES.DATA_WRANGLER, PRODUCT_TYPES.QUICK_QUERY].includes(
          this.resourceTypeModel
        ) &&
          this.childResourceTypeModel !== 'namespace')
      ) {
        this.getKubernetesItems();
      } else if (
        this.resourceTypeModel === PRODUCT_TYPES.LOAD_BALANCER &&
        this.childResourceTypeModel !== 'service'
      ) {
        this.getLoadBalancerItems();
      } else if (
        (this.resourceTypeModel === PRODUCT_TYPES.DATA_OPS ||
          this.resourceTypeModel === PRODUCT_TYPES.DATA_FLOW) &&
        this.childResourceTypeModel !== 'service'
      ) {
        this.getDataOpsFlowtems();
      }
    },

    /** 성능항목 셋팅 */
    async setMetricDatas(comparison) {
      if (this.resourceTypeModelTmt && this.resourceTypeModelTmt === this.resourceTypeModel) return;
      let params = {
        targetSqs: _.map(this.productModel, (m) => m.productSq)
      };

      if (this.childResourceTypeModel) params.objectType = this.childResourceTypeModel;
      await getMetricDatas(this.resourceTypeModel, params).then((res) => {
        let data = res.contents;
        let tmp = {};
        let result = [];

        data.forEach((item) => {
          if (params.objectType) {
            item.objectType = params.objectType;
          }
          if (tmp[item.metricSetName] == null) tmp[item.metricSetName] = [];
          tmp[item.metricSetName].push(item);
        });
        Object.keys(tmp).forEach((item) => {
          let child = tmp[item].map((m) => {
            m.value = m.metricKey;
            m.text = m.metricName;
            m.check = false;
            if (this.selectedMetrics.length && m.metricKey === this.selectedMetrics[0].metricKey) {
              m.check = true;
            }
            if (comparison) {
              this.comparisonSelectedMetrics(m);
            }

            return m;
          });
          result.push({ value: item, text: item, metricName: item, childItems: child });
        });
        this.metrics = result;
        this.resourceTypeModelTmt = this.resourceTypeModel;
      });
    },
    comparisonSelectedMetrics(metric) {
      const options = this.defaultConfig.chartAttr.widget.options;
      if (options.metricKey === metric.metricKey) {
        options.isObjectExist = metric.isObjectExist;
        options.disableObject = metric.disableObject;
        options.metricName = metric.metricName;
        this.defaultConfig.chartAttr.widget.options = options;
      }
    },

    /** k8s 하위 상품 정보 */
    getKubernetesItems() {
      let params = {
        productIdList: _.map(this.productModel, (m) => m.productSq),
        productObjectType: this.childResourceTypeModel,
        queryStartDt: this.searchDate.queryStartDt,
        queryEndDt: this.searchDate.queryEndDt
      };
      getObjectDatas('k8s', this.resourceTypeModel, params).then((res) => {
        this.setObjectTypes(res);
      });
    },

    /** LB 하위 상품 정보 */
    getLoadBalancerItems() {
      let params = {
        productIds: _.map(this.productModel, (m) => m.productSq),
        queryStartDt: this.searchDate.queryStartDt,
        queryEndDt: this.searchDate.queryEndDt
      };
      getObjectDatas('lb', this.resourceTypeModel, params).then((res) => {
        this.setObjectTypes(res.contents);
      });
    },

    /** 하위 상품 정보 */
    getDataOpsFlowtems() {
      let params = {
        productIds: _.map(this.productModel, (m) => m.productSq),
        queryStartDt: this.searchDate.queryStartDt,
        queryEndDt: this.searchDate.queryEndDt
      };
      getObjectDatas('dataops-dataflow', this.resourceTypeModel, params).then((res) => {
        this.setObjectTypes(res.contents);
      });
    },

    /** 하위 상품 정보 */
    setObjectTypes(datas) {
      this.objectTypeItems = this.$util.replaceArr(datas, {
        objectName: 'key',
        objectDisplayName: 'text',
        productResourceId: 'productResourceId'
      });
      this.objectTypeModel = this.objectTypeModel.filter((item) => {
        return this.objectTypeItems.some((other) => other.key === item.key);
      });
    },

    /** 개별항목 셋팅 */
    async setObjectItems(metricKey) {
      if (this.productModel.length === 0 || !this.isResourceTypeAuth) {
        return;
      }
      let params = {
        metricKeyList: [metricKey],
        productIdList: this.productModel.map((m) => m.productSq),
        queryStartDt: this.searchDate.queryStartDt,
        queryEndDt: this.searchDate.queryEndDt
      };

      await getObjectDatas('', this.resourceTypeModel, params).then((res) => {
        if (res.length) {
          const isFileSystem = params.metricKeyList.includes('system.filesystem.used.pct');
          const processedRes = this.$http.product.afterCallObjects(res, isFileSystem);

          this.objectItems = this.$util.replaceArr(processedRes, {
            objectName: 'key',
            objectDisplayName: 'text'
          });

          this.objectModel = this.objectModel.filter((item) => {
            return this.objectItems.some((other) => other.key === item.key);
          });
        }
      });
    },

    // 위젯 초기 기본정보 설정
    async setDefault() {
      this.resourceTypeModel = this.defaultConfig.chartAttr.widget.options.resourceType || ''; // 상품유형

      let config = this.defaultConfig;
      let options = config.chartAttr.widget.options;

      this.widgetName = config.chartAttr.title; // 위젯명
      this.productModel = options.products || []; // 모니터링대상 상품
      this.objectTypeModel = options.objectItems || []; // 모니터링대상 하위 상품
      this.disableIndividualItem = options.disableIndividualItem;
      // 하위 상품 유형
      this.childResourceTypeModel = options.objectType || '';

      if (this.isAccountAuth) {
        await this.setResourceTypeData();
        this.isResourceTypeAuth = await this.checkProductSubTypeAuth(this.resourceTypeModel);
      }

      await this.setMetricDatas(true);

      if (this.childResourceTypeModel) {
        await this.setChildProductsDatas();
        if ((this.isModifyMode || this.isExternalAddMode) && !options.disableIndividualItem) {
          await this.getIndividualItemData();
        }
      }

      if (options.metricKey && this.isResourceTypeAuth) {
        this.selectedMetrics = [options]; // 성능항목
        if (options.isObjectExist === 'Y' && !this.isChildResourceType) {
          await this.setObjectItems(options.metricKey); // 개별항목 목록 가져오기
        }
      }

      this.objectModel = options.objects || []; // 개별항목

      //TODO check
      if (options.isDetailPerformance) this.objectModel = this.getListIndividualItem;

      if (options.targetsMethod) {
        this.useTargetsMethod = true;
        this.targetsMethodModel = options.targetsMethod;
      }

      // 위험구간 설정
      if (options.thresholdStart && options.thresholdEnd) {
        this.showThresholdRange = true;
        this.thresholdStart = options.thresholdStart;
        this.thresholdEnd = options.thresholdEnd;
      }
      if (options.thresholdValue && options.comparisonOperator) {
        this.showThresholdValue = true;
        this.thresholdValue = options.thresholdValue;
        this.comparisonOperator = options.comparisonOperator;
      }

      // 성능비교에서 시계열 그래프 추가 할 경우 대시보드 목록을 가져온다.
      if (this.isExternalAddMode) {
        await this.setDashboardData();
      }

      await this.getListProductAndPerformanceItem(false, true);
    },

    setMetricDataForPerformanceItem(metricKey, metricName) {
      let config = this.getCommonConfig();
      config.chartAttr.widget.options.metricKey = metricKey;
      config.chartAttr.widget.options.metricName = metricName;
      this.tempConfigInfos = [config];
    },

    // 위젯 설정 정보 update
    onConfigChange(isApiCall = true) {
      let configs = [];
      this.checkCount++;

      let config = this.getCommonConfig();
      config.chartAttr.title = this.widgetName;
      config.isApiCall = isApiCall;

      if (this.isExternalAddMode) config.dashboardId = this.dashboardModel;
      let options = config.chartAttr.widget.options;
      if (this.chartType) {
        options.chartType = this.chartType;
      }
      options.resourceType = this.resourceTypeModel;
      options.products = this.productModel;
      options.metricKey = this.selectedMetrics[0] ? this.selectedMetrics[0].metricKey : '';
      options.metricName = this.selectedMetrics[0] ? this.selectedMetrics[0].metricName : '';
      options.disableObject = this.selectedMetrics[0] ? this.selectedMetrics[0].disableObject : '';
      options.objects = this.objectModel;
      options.objectType = this.isChildResourceType ? this.childResourceTypeModel : '';
      options.objectItems = this.objectTypeModel;
      options.isK8sObjectType = this.isK8sObjectType;
      options.disableIndividualItem = this.disableIndividualItem;
      if (this.useTargetsMethod) {
        options.targetsMethod = this.targetsMethodModel;
      }

      let thresholdStartDot = this.$util.deepCopy(this.thresholdStart);
      let thresholdEndDot = this.$util.deepCopy(this.thresholdEnd);
      if (thresholdStartDot === '.') thresholdStartDot = '.0';
      if (thresholdEndDot === '.') thresholdEndDot = '.0';

      if (
        this.showThresholdRange &&
        thresholdStartDot &&
        thresholdEndDot &&
        parseFloat(thresholdEndDot) > thresholdStartDot
      ) {
        options.thresholdStart = thresholdStartDot;
        options.thresholdEnd = thresholdEndDot;
      } else {
        delete options['thresholdStart'];
        delete options['thresholdEnd'];
      }
      if (this.showThresholdValue) {
        options.thresholdValue = this.thresholdValue;
        options.comparisonOperator = this.comparisonOperator;
        if (this.showThresholdValue === '.') options.thresholdValue = '.0';
      } else {
        delete options['thresholdValue'];
        delete options['comparisonOperator'];
      }

      options.isObjectExist = this.isObjectExist;
      configs.push(config);

      let configInfos = [];
      if (this.$util.isNotEmpty(this.selectedMetrics[0])) {
        if (
          (!this.isChildResourceType && this.objectItems.length > 0 && !this.objectModel.length) ||
          !this.productModel.length
        ) {
          configInfos = [];
        } else {
          configInfos = configs;
        }
      }

      this.tempConfigInfos = configs;
      if (this.checkCount === 2) {
        this.originConfigs = configs;
      }
      this.$emit('configModify', configInfos);
    },

    /** 선택값 단계별 초기화 */
    reset(i = 6) {
      if (i > 5) {
        // 프로젝트 선택시
        this.resourceTypeItems = [];
        this.resourceTypeModel = '';
      }
      if (i > 4) {
        // 상품유형 선택시
        this.childResourceTypeItems = [];
        this.childResourceTypeModel = '';
      }
      if (i > 3) {
        // 상품 하위 유형 선택시
        this.productItems = [];
        this.productModel = [];
      }

      if (i > 2) {
        // 상품(모니터링 대상) 선택시
        this.metrics = [];
        this.resourceTypeModelTmt = '';
        this.metricModel = {};
        this.selectedMetrics = [];
        this.objectTypeItems = [];
        this.objectTypeModel = [];
      }

      if (i > 1) {
        // 성능항목 선택
        this.objectItems = [];
        this.objectModel = [];
        this.listIndividual = [];
      }
    },

    /******** validation check ***********/
    // 대시보드 선택 validate check
    checkDashboardModel(value) {
      if (!this.checkEmpty(value)) {
        return this.$t('message.select2', [this.$t('label.dashboard')]);
      }
      return true;
    },

    // 상품 선택 validate check
    checkResourceTypeModel() {
      if (!this.isResourceTypeAuth) return '';
      return true;
    },

    // 모니터링 대상 validate check
    checkProductModel(value) {
      if (!this.isProductAuth) return true;

      if (value.length > 10 && !this.isCheckObjectTypeItems) {
        return this.$t('message.selectCount', ['10']); // 10개 까지만 선택 가능합니다.
      }
      return true;
    },
    // 모니터링 대상 개별항목 validate check
    checkObjectTypeModel(value) {
      if (!this.isProductAuth) return true;
      if (this.validating) {
        if (!value.length) {
          return this.$t('message.select1', [
            this.$t('label.monitoring') + ' ' + this.$t('label.target')
          ]); // '개별항목을 선택하세요.';
        }
      }
      return true;
    },
    // 개별항목 체크
    checkObjectModel(value) {
      if (!this.isProductAuth) return '';
      if (this.validating) {
        if (!value.length) {
          return this.$t('message.select1', [this.$t('label.individualItem')]); // '개별항목을 선택하세요.';
        }
      }
      return true;
    },
    // 위험구간 시작 validate check
    checkThresholdStart(value) {
      if (!this.checkEmpty(value)) {
        this.isValidMessage.useThresholdRange = true;
        this.isValidMessage.useThresholdRangeText = this.$t('message.requiredInput');
        return '';
      }
      if (!this.checkOnlyNumberDot(value)) {
        this.isValidMessage.useThresholdRange = true;
        this.isValidMessage.useThresholdRangeText = this.$t('message.errorValidateInput');
        return '';
      }
      if (this.compileThresoldRange()) return '';
      if (this.$refs.thresholdEnd.hasError) {
        this.$util.validate([this.$refs.thresholdEnd]);
      }
      return true;
    },
    // 위험구간 종료 validate check
    checkThresholdEnd(value) {
      if (!this.checkEmpty(value)) {
        this.isValidMessage.useThresholdRange = true;
        this.isValidMessage.useThresholdRangeText = this.$t('message.requiredInput');
        return '';
      }
      if (!this.checkOnlyNumberDot(value)) {
        this.isValidMessage.useThresholdRange = true;
        this.isValidMessage.useThresholdRangeText = this.$t('message.errorValidateInput');
        return '';
      }
      if (this.compileThresoldRange()) return '';
      if (this.$refs.thresholdStart.hasError) {
        this.$util.validate([this.$refs.thresholdStart]);
      }
      return true;
    },
    // 위험구간 종료 validate check
    checkThresholdValue(value) {
      if (!this.checkEmpty(value)) {
        this.isValidMessage.thresholdValue = true;
        this.isValidMessage.thresholdValueText = this.$t('message.requiredInput');
        return '';
      }
      if (!this.checkOnlyNumberDot(value)) {
        this.isValidMessage.thresholdValue = true;
        this.isValidMessage.thresholdValueText = this.$t('message.errorValidateInput');
        return '';
      }
      this.isValidMessage.thresholdValue = false;
      return true;
    },
    compileThresoldRange() {
      // 시작값이 종료값보다 작습니다.=
      if (
        !this.checkOnlyNumberDot(this.thresholdStart) ||
        !this.checkOnlyNumberDot(this.thresholdEnd)
      )
        return false;

      if (parseFloat(this.thresholdEnd) <= parseFloat(this.thresholdStart)) {
        this.isValidMessage.useThresholdRange = true;
        this.isValidMessage.useThresholdRangeText = this.$t('page.widget.riskRangeCheck');
        return true;
      }
      return false;
    },
    // 위험도 이상&이하 validate check
    checkComparisonOperator() {
      return this.comparisonOperator === 'GE' || this.comparisonOperator === 'LE';
    },
    checkOnlyNumberDot(text) {
      return /^([1-9]\d*|0)(\.\d{1,2})?$/g.test(text);
    },
    // 위험도 validation check
    checkUseThresholdRange() {
      this.isValidMessage.useThresholdRange = false;
      this.isValidMessage.useThresholdRangeText = '';
      if (this.showThresholdRange) {
        if (!this.checkOnlyNumberDot(this.thresholdStart)) {
          // 시작값을 입력하세요.
          return false;
        }
        if (!this.checkOnlyNumberDot(this.thresholdEnd)) {
          // 종료값을 입력하세요.
          return false;
        }

        if (parseFloat(this.thresholdEnd) <= parseFloat(this.thresholdStart)) {
          return false;
        }
        this.$nextTick(() =>
          this.$util.validate([this.$refs.thresholdStart, this.$refs.thresholdEnd])
        );
      }

      return true;
    },

    validate() {
      this.validating = true;

      if (!this.$util.validate([this.$refs.widgetName])) {
        return false;
      }

      if (this.isExternalAddMode && !this.$util.validate([this.$refs.dashboardModel])) {
        return false;
      }

      if (!this.$util.validate([this.$refs.productModel])) {
        return false;
      }

      if (this.showObjectTypeItems) {
        if (!this.$util.validate([this.$refs.objectTypeModel])) return false;
      }

      if (this.selectedMetrics.length > 0) {
        this.isValidMessage.metricModel = false;
      } else {
        this.isValidMessage.metricModel = true;
        return false;
      }

      if (
        (this.isObjectExist === 'Y' && !this.isChildResourceType) ||
        (!this.disableIndividualItem && this.$util.isNotEmpty(this.getListIndividualItem))
      ) {
        if (!this.$util.validate([this.$refs.objectModel])) return false;
      }

      if (this.useTargetsMethod && this.targetsMethodModel === '') {
        this.isValidMessage.targetsMethodModel = true;
        return false;
      } else {
        this.isValidMessage.targetsMethodModel = false;
      }

      if (this.showThresholdRange) {
        if (!this.$util.validate([this.$refs.thresholdStart])) {
          return false;
        }
        if (!this.$util.validate([this.$refs.thresholdEnd])) {
          return false;
        }
      }

      if (this.showThresholdValue) {
        if (!this.$util.validate([this.$refs.thresholdValue])) {
          return false;
        }
        if (!this.$util.validate([this.$refs.comparisonOperator])) {
          return false;
        }
      }
      return true;
    },

    // validation check reset
    resetValidate(mode) {
      this.resetSearchField(mode);
      this.validating = false;
      this.isValidMessage.metricModel = false;
      this.isValidMessage.targetsMethodModel = false;

      let refs = [this.$refs.resourceTypeModel, this.$refs.productModel];
      if (this.isExternalAddMode) refs.push(this.$refs.dashboardModel);
      if (this.showObjectTypeItems && this.$refs.objectTypeModel)
        refs.push(this.$refs.objectTypeModel);
      if (this.objectItems.length > 0) refs.push(this.$refs.objectModel);
      if (this.use)
        if (this.useThresholdRange && this.showThresholdRange) {
          if (this.$refs.thresholdStart) refs.push(this.$refs.thresholdStart);
          if (this.$refs.thresholdEnd) refs.push(this.$refs.thresholdEnd);
        }
      if (this.useThresholdValue && this.showThresholdValue) {
        if (this.$refs.thresholdValue) refs.push(this.$refs.thresholdValue);
        if (this.$refs.comparisonOperator) refs.push(this.$refs.comparisonOperator);
      }
      this.$util.clearValidate(refs);
    },

    // input number check
    checkNumberInput() {
      this.$nextTick(() => {
        if (this.checkUseThresholdRange()) this.onConfigChange();
      });
    },

    isDirtyCheck() {
      return JSON.stringify(this.originConfigs) !== JSON.stringify(this.tempConfigInfos);
    },

    validateBtnComfirm() {
      if (this.checkWidgetName(this.widgetName) !== true) return true;

      // check -> Select Dashboard *
      if (this.isExternalAddMode && this.checkDashboardModel(this.dashboardModel) !== true)
        return true;

      // check -> Product type*
      if (
        this.checkResourceTypeModel(this.resourceTypeModel) !== true &&
        this.$util.isEmpty(this.resourceTypeModel)
      )
        return true;

      // check -> Monitoring target *
      if (this.checkProductModel(this.productModel) !== true || this.productModel.length === 0)
        return true;

      if (this.checkEmptyObjectTypeItems) return true;

      // check -> Performance item*
      if (this.selectedMetrics.length === 0) return true;

      // check -> Individual Item
      if (
        !this.disableIndividualItem &&
        this.selectedMetrics.length &&
        this.objectModel.length === 0
      )
        return true;

      // check -> Statistics
      if (this.useTargetsMethod && this.$util.isEmpty(this.targetsMethodModel)) return true;

      if (this.useThresholdRange && this.showThresholdRange && !this.checkUseThresholdRange())
        return true;

      if (this.useThresholdValue && this.showThresholdValue) {
        if (
          !this.checkEmpty(this.thresholdValue) ||
          !this.checkOnlyNumberDot(this.thresholdValue)
        ) {
          return true;
        }
        if (this.$util.isEmpty(this.comparisonOperator)) return true;
      }

      return false;
    },
    resetSearchField(mode = 'all') {
      const refs = ['productModel', 'objectTypeModel', 'metricModel', 'objectModel'].filter(
        (e) => e !== mode
      );
      for (let i in refs) {
        this.$refs[refs[i]] && this.$refs[refs[i]].filterTextOnInput('');
      }
    },
    changeHeightDownwrap() {
      this.isChangeBolet = !this.isChangeBolet;
    }
  }
};
</script>
<style lang="scss" scoped>
:deep {
  .product-model {
    margin-top: 0 !important;
  }

  .erorr-message {
    margin-top: -8px !important;
  }
}

:deep {
  .performance-item-container {
    .search-box {
      padding-right: 0px;
    }
  }
}

.area {
  margin-bottom: 24px;

  .product-model {
    :deep {
      .lego-validator--error {
        top: -18px;
      }
    }
  }
}

.area.monitoring-target :deep {
  .lego-validator--error {
    &.product-model {
      margin-top: 14px;

      .lego-validator__message {
        top: -18px;
      }
    }

    &.object-type-model {
      .lego-validator__message {
        top: -18px;
      }
    }
  }
}

.check_vertical {
  width: 100%;
  margin-top: 9px;
}

.message {
  margin-left: 10px;
  color: #fc434e;
  font-size: 12px;
  text-align: right;
}

.performance .message {
  margin-left: 0px;
  margin-bottom: -6px;
}

.lego-radio-wrapper .lego-radio--medium + .lego-radio--medium {
  margin-left: 10px !important;
}
</style>
